import { unique, type BookingDto, type RydeUiUnit } from "@ryde/shared";

/**
 * Resolves the ids a bulk action (update, archive, cancel) runs on from the page's selection.
 *
 * Grids keep their selection in the flat row-ids atom, and grouped server pages keep no DTOs at all, so
 * there the id list is the selection. The bookings calendar is the exception: it keeps its selection as
 * passenger+day cells and resolves them to bookings only on read, so in that view the ids must come from
 * the resolved bookings. Reading the ids atom there yields an empty list, and the bulk update drawer then
 * sends `ids: []`, which the backend rejects as a failed process (RYDE-8513).
 *
 * `isBookingCalendar` is computed once by the caller (`entity === "booking"` on the calendar route) so the ids
 * and the selected values cannot disagree on which view is active. The calendar ids are deduplicated because
 * "one booking per selected cell" is only an implicit invariant of `selectedCellsBookingsSelector`, and a
 * repeated id would go straight into the bulk request.
 */
export const resolveSelectedRowsIds = ({
  entity,
  isBookingCalendar,
  reportSelectedInnerDataIds,
  selectedGridIds,
  selectedBookingsCalender,
}: {
  entity: RydeUiUnit;
  isBookingCalendar: boolean;
  reportSelectedInnerDataIds: number[];
  selectedGridIds: number[];
  selectedBookingsCalender: Pick<BookingDto, "id">[];
}): number[] => {
  if (entity === "report") {
    return reportSelectedInnerDataIds;
  }

  if (isBookingCalendar) {
    return unique(selectedBookingsCalender.map(({ id }) => id));
  }

  return selectedGridIds;
};
